import React from 'react';
import { formatYear } from '../utils/dateUtils';

/**
 * ZoomController - UI controls for zooming in/out on the timeline.
 * Displays the current visible year range and provides buttons to narrow or widen it.
 *
 * @param {object} props
 * @param {{ start: number, end: number }} props.visibleRange - The current visible year range
 * @param {function} props.onRangeChange - Callback to update the visible range (start, end)
 */
function ZoomController({ visibleRange, onRangeChange }) {
  const rangeSize = visibleRange.end - visibleRange.start;
  const center = visibleRange.start + rangeSize / 2;

  const MINIMUM_RANGE = 10;

  /**
   * Zoom In: narrows the visible range by multiplying by 0.8, centered.
   * Enforces a minimum range of 10 years.
   */
  const handleZoomIn = () => {
    const newRangeSize = Math.max(MINIMUM_RANGE, Math.round(rangeSize * 0.8));
    const newStart = Math.round(center - newRangeSize / 2);
    const newEnd = newStart + newRangeSize;
    onRangeChange(newStart, newEnd);
  };

  /**
   * Zoom Out: widens the visible range by multiplying by 1.2, centered.
   */
  const handleZoomOut = () => {
    const newRangeSize = Math.round(rangeSize * 1.2);
    const newStart = Math.round(center - newRangeSize / 2);
    const newEnd = newStart + newRangeSize;
    onRangeChange(newStart, newEnd);
  };

  const isZoomInDisabled = rangeSize <= MINIMUM_RANGE;

  return (
    <div className="zoom-controller" role="toolbar" aria-label="Zoom controls">
      <button
        className="zoom-controller-btn zoom-in-btn"
        onClick={handleZoomIn}
        disabled={isZoomInDisabled}
        aria-label="Zoom in"
        title="Zoom in (narrow range)"
      >
        +
      </button>
      <button
        className="zoom-controller-btn zoom-out-btn"
        onClick={handleZoomOut}
        aria-label="Zoom out"
        title="Zoom out (widen range)"
      >
        −
      </button>
      <div className="zoom-controller-info" aria-live="polite">
        <span className="zoom-range-display">
          {formatYear(visibleRange.start)} – {formatYear(visibleRange.end)}
        </span>
        <span className="zoom-range-size">
          ({rangeSize} years)
        </span>
      </div>
    </div>
  );
}

export default ZoomController;
